/**
 * Vector Labs — Blog API Layer
 *
 * Promise-based wrapper that talks to the local Express server when
 * available, and falls back to the in-browser localStorage store.
 */

import {
  getAllPosts,
  getPostBySlug,
  getPostById,
  searchPosts,
  createPost,
  updatePost,
  deletePost,
  BLOG_CATEGORIES,
} from '../data/blogStore';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LATENCY = 120;

function delay(ms = LATENCY) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Check if the server is reachable */
let _serverAvailable = null;
async function serverAvailable() {
  if (_serverAvailable !== null) return _serverAvailable;
  try {
    const r = await fetch(`${API_BASE}/api/health`, { signal: AbortSignal.timeout(1200) });
    _serverAvailable = r.ok;
  } catch {
    _serverAvailable = false;
  }
  // Re-check periodically
  setTimeout(() => { _serverAvailable = null; }, 30000);
  return _serverAvailable;
}

function authHeaders() {
  const stored = localStorage.getItem('fvl_auth');
  if (stored) {
    try {
      const user = JSON.parse(stored);
      if (user.credential) return { Authorization: `Bearer ${user.credential}` };
      if (user.email) return { 'x-admin-email': user.email };
    } catch { /* ignore */ }
  }
  return {};
}

// ─── GET /api/blog ──────────────────────────────────────────────
export async function apiGetAllPosts({ status, category, q, featured } = {}) {
  if (await serverAvailable()) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    if (featured) params.set('featured', 'true');
    const r = await fetch(`${API_BASE}/api/blog?${params}`);
    return r.json();
  }
  // Fallback
  await delay();
  let results = getAllPosts();
  if (status) results = results.filter((p) => p.status === status);
  if (category) results = results.filter((p) => p.category === category);
  if (featured) results = results.filter((p) => p.featured);
  if (q) results = searchPosts(q);
  return { ok: true, total: results.length, data: results };
}

// ─── GET /api/blog/:idOrSlug ────────────────────────────────────
export async function apiGetPost(idOrSlug) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/blog/${idOrSlug}`);
    return r.json();
  }
  await delay();
  const post = getPostBySlug(idOrSlug) || getPostById(idOrSlug);
  if (!post) return { ok: false, error: 'Not found' };
  return { ok: true, data: post };
}

// ─── POST /api/blog ─────────────────────────────────────────────
export async function apiCreatePost(body) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/blog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    return r.json();
  }
  await delay();
  if (!body.title) return { ok: false, error: 'Missing required field: title' };
  const post = createPost(body);
  return { ok: true, data: post, status: 201 };
}

// ─── PUT /api/blog/:id ──────────────────────────────────────────
export async function apiUpdatePost(id, body) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/blog/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    return r.json();
  }
  await delay();
  const post = updatePost(id, body);
  if (!post) return { ok: false, error: 'Not found' };
  return { ok: true, data: post };
}

// ─── DELETE /api/blog/:id ───────────────────────────────────────
export async function apiDeletePost(id) {
  if (await serverAvailable()) {
    await fetch(`${API_BASE}/api/blog/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return { ok: true };
  }
  await delay();
  deletePost(id);
  return { ok: true };
}

// ─── GET /api/blog-categories ───────────────────────────────────
export async function apiGetBlogCategories() {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/blog-categories`);
    return r.json();
  }
  await delay();
  return { ok: true, data: BLOG_CATEGORIES };
}

// ─── Upload file ────────────────────────────────────────────────
export async function apiUploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const r = await fetch(`${API_BASE}/api/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  return r.json();
}

// ─── POST /api/blog/reset ───────────────────────────────────────
export async function apiResetBlog() {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/blog/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return r.json();
  }
  // Fallback to localStorage
  await delay();
  localStorage.removeItem('vector_labs_blog');
  return { ok: true, message: 'Blog data has been reset.' };
}
